<template>
	<a-modal
		:visible="visible"
		title="模板设置"
		:confirm-loading="saving"
		ok-text="保存"
		cancel-text="取消"
		:destroy-on-close="true"
		:centered="true"
		:width="640"
		:z-index="2200"
		wrap-class-name="g-tpl-settings-modal"
		@ok="onSave"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
			<a-form layout="vertical" class="g-tpl-settings">
				<a-form-item label="封面图">
					<div class="g-tpl-settings__cover">
						<div class="g-tpl-settings__cover-preview">
							<img v-if="coverPreviewUrl" :src="coverPreviewUrl" alt="封面" />
							<span v-else class="g-tpl-settings__cover-empty">暂无封面</span>
						</div>
						<div class="g-tpl-settings__cover-actions">
							<a-button
								type="primary"
								ghost
								size="small"
								:loading="uploading"
								@click="triggerCoverSelect"
							>{{ coverPreviewUrl ? '更换封面' : '上传封面' }}</a-button>
							<a-button
								v-if="coverPreviewUrl"
								size="small"
								:disabled="uploading"
								@click="clearCover"
							>清除</a-button>
							<p class="g-tpl-settings__cover-tip">建议竖图，比例接近 17:25</p>
						</div>
					</div>
				</a-form-item>
				<a-form-item label="标题">
					<a-input v-model:value="form.title" placeholder="请输入标题" maxlength="100" />
				</a-form-item>
				<a-form-item label="副标题">
					<a-input
						v-model:value="form.content.subtitle"
						placeholder="显示在标题下方"
						maxlength="200"
					/>
				</a-form-item>
				<a-form-item label="是否热门">
					<a-radio-group v-model:value="form.content.isHot">
						<a-radio :value="true">显示热门标签</a-radio>
						<a-radio :value="false">不显示</a-radio>
					</a-radio-group>
				</a-form-item>
				<a-form-item label="所属类别">
					<a-select
						v-model:value="form.tag_ids"
						mode="multiple"
						allow-clear
						show-search
						option-filter-prop="label"
						placeholder="请选择模板所属类别"
						:loading="tagsLoading"
						:get-popup-container="getSelectPopupContainer"
						:dropdown-style="{ zIndex: 2300 }"
						style="width: 100%"
					>
						<a-select-option
							v-for="item in tagOptions"
							:key="item.value"
							:value="item.value"
							:label="item.label"
						>{{ item.label }}</a-select-option>
					</a-select>
				</a-form-item>
				<a-form-item label="关键词">
					<a-input v-model:value="form.keywords" placeholder="SEO 关键词" maxlength="200" />
				</a-form-item>
				<a-form-item label="描述">
					<a-textarea v-model:value="form.description" placeholder="SEO 描述" :rows="3" maxlength="300" />
				</a-form-item>

				<a-form-item label="特征设置（3 项）">
					<div class="g-tpl-settings__group">
						<div
							v-for="(feature, index) in form.content.features"
							:key="`feature-${index}`"
							class="g-tpl-settings__row"
						>
							<div class="g-tpl-settings__row-label">特征 {{ index + 1 }}</div>
							<a-input
								v-model:value="feature.title"
								placeholder="标题"
								maxlength="40"
							/>
							<a-input
								v-model:value="feature.subtitle"
								placeholder="副标题"
								maxlength="40"
							/>
						</div>
					</div>
				</a-form-item>

				<a-form-item label="标签区域">
					<div class="g-tpl-settings__group">
						<div
							v-for="(stat, index) in form.content.stats"
							:key="`stat-${index}`"
							class="g-tpl-settings__row g-tpl-settings__row--stat"
						>
							<div class="g-tpl-settings__row-head">
								<span class="g-tpl-settings__row-label">标签 {{ index + 1 }}</span>
								<a-button
									v-if="form.content.stats.length > 1"
									type="link"
									size="small"
									danger
									@click="removeStat(index)"
								>删除</a-button>
							</div>
							<a-input
								v-model:value="stat.icon"
								placeholder="iconfont 类名，如 mbm-tuandui"
								maxlength="60"
							>
								<template #prefix>
									<i
										v-if="stat.icon"
										class="mbm-iconfont g-tpl-settings__icon-preview"
										:class="stat.icon"
									></i>
								</template>
							</a-input>
							<a-input
								v-model:value="stat.title"
								placeholder="标题"
								maxlength="40"
							/>
							<a-input
								v-model:value="stat.subtitle"
								placeholder="副标题"
								maxlength="40"
							/>
						</div>
						<a-button
							v-if="form.content.stats.length < 6"
							type="dashed"
							block
							@click="addStat"
						>添加标签</a-button>
					</div>
				</a-form-item>

				<a-form-item label="排序（越大越靠前）">
					<a-input-number v-model:value="form.sort" :min="0" style="width: 100%" />
				</a-form-item>
				<a-form-item label="状态">
					<a-radio-group v-model:value="form.status">
						<a-radio :value="1">启用</a-radio>
						<a-radio :value="0">禁用</a-radio>
					</a-radio-group>
				</a-form-item>
				<a-form-item label="置顶">
					<a-radio-group v-model:value="form.stick">
						<a-radio :value="0">默认</a-radio>
						<a-radio :value="1">置顶</a-radio>
					</a-radio-group>
				</a-form-item>
			</a-form>
		</div>
		<input
			ref="coverInput"
			type="file"
			accept="image/*"
			class="g-tpl-settings__file-input"
			@change="onCoverChange"
		/>
	</a-modal>
</template>

<script>
	import { message } from 'ant-design-vue';
	import {
		createDefaultTplCardContent,
		normalizeTplTagIds,
		parseTplCardContent,
		resolveTplTextField,
	} from '@/gEdit/utils/pageTemplate.js';
	import { filterValidLocalFiles } from '@/gEdit/utils/localImage.js';
	import { uploadHeaderMediaFiles } from '@/gEdit/utils/uploadHeaderMedia.js';

	export default {
		name: 'GEditTemplateSettingsModal',
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			template: {
				type: Object,
				default: null,
			},
		},
		emits: ['update:visible', 'saved'],
		data() {
			return {
				saving: false,
				uploading: false,
				tagsLoading: false,
				tagOptions: [],
				coverPreviewUrl: '',
				form: {
					id: null,
					title: '',
					keywords: '',
					description: '',
					sort: 0,
					cover_attach_id: 0,
					status: 1,
					stick: 0,
					tag_ids: [],
					content: createDefaultTplCardContent(),
				},
			};
		},
		watch: {
			visible(value) {
				if (value) {
					this.initForm();
					this.fetchTags();
				}
			},
		},
		methods: {
			initForm() {
				const item = this.template || {};
				const title = resolveTplTextField(item.title);
				const content = parseTplCardContent(item.content);
				this.coverPreviewUrl = item.cover || item.qrcode_path || item.qrcode_url || '';
				this.form = {
					id: item.id,
					title: String(item.title || '').trim(),
					keywords: item.keywords || title,
					description: item.description || title,
					sort: Number(item.sort) || 0,
					cover_attach_id: Number(item.cover_attach_id) || 0,
					status: item.status === 0 ? 0 : 1,
					stick: item.stick === 1 ? 1 : 0,
					tag_ids: normalizeTplTagIds(item.tag_ids || item.tags),
					content: {
						isHot: content.isHot,
						subtitle: content.subtitle,
						features: content.features.map((item) => ({ ...item })),
						stats: content.stats.map((item) => ({ ...item })),
					},
				};
			},
			fetchTags() {
				if (this.tagsLoading) return;
				this.tagsLoading = true;
				this.$apiRequest.post('/tpl/tags', {}).then((res) => {
					this.tagsLoading = false;
					if (Number(res.data.code) !== 200) {
						this.$baseFunc.checkState(res.data);
						return;
					}
					const raw = res.data.data;
					const list = Array.isArray(raw)
						? raw
						: (Array.isArray(raw?.list) ? raw.list : (Array.isArray(raw?.data) ? raw.data : []));
					this.tagOptions = list.map((item) => {
						const value = Number(item?.id ?? item?.value);
						const label = String(item?.name ?? item?.title ?? item?.label ?? value);
						return { value, label };
					}).filter((item) => Number.isFinite(item.value) && item.value > 0);
				}).catch((err) => {
					this.tagsLoading = false;
					if (err?.data) this.$baseFunc.checkState(err.data);
				});
			},
			getSelectPopupContainer(triggerNode) {
				return triggerNode?.parentNode || document.body;
			},
			addStat() {
				if (this.form.content.stats.length >= 6) return;
				this.form.content.stats.push({
					icon: 'mbm-tuandui',
					title: '',
					subtitle: '',
				});
			},
			removeStat(index) {
				if (this.form.content.stats.length <= 1) return;
				this.form.content.stats.splice(index, 1);
			},
			triggerCoverSelect() {
				this.$refs.coverInput.value = '';
				this.$refs.coverInput.click();
			},
			async onCoverChange(event) {
				const file = (event.target.files || [])[0];
				event.target.value = '';
				const validFiles = filterValidLocalFiles(file ? [file] : [], 1);
				if (!validFiles.length) return;

				this.uploading = true;
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: validFiles,
					});
					const attachId = Number(item?.id) || 0;
					if (!attachId) {
						message.error('上传成功但未返回附件 ID');
						return;
					}
					this.form.cover_attach_id = attachId;
					this.coverPreviewUrl = item.previewUrl || item.url || '';
					message.success('封面上传成功');
				} catch (error) {
					message.error('封面上传失败');
				} finally {
					this.uploading = false;
				}
			},
			clearCover() {
				this.form.cover_attach_id = 0;
				this.coverPreviewUrl = '';
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			buildContentPayload() {
				const content = parseTplCardContent(this.form.content);
				return {
					isHot: !!content.isHot,
					subtitle: content.subtitle,
					features: content.features.map((item) => ({
						title: item.title,
						subtitle: item.subtitle,
					})),
					stats: content.stats
						.filter((item) => item.title || item.subtitle || item.icon)
						.map((item) => ({
							icon: item.icon,
							title: item.title,
							subtitle: item.subtitle,
						})),
				};
			},
			onSave() {
				if (!this.form.id) return;
				const title = resolveTplTextField(this.form.title);
				const content = this.buildContentPayload();
				const tagIds = normalizeTplTagIds(this.form.tag_ids);
				this.saving = true;
				this.$apiRequest.post('/tpl/edit', {
					id: this.form.id,
					title,
					keywords: resolveTplTextField(this.form.keywords) === ' '
						? title
						: resolveTplTextField(this.form.keywords),
					description: resolveTplTextField(this.form.description) === ' '
						? title
						: resolveTplTextField(this.form.description),
					sort: Number(this.form.sort) || 0,
					cover_attach_id: Number(this.form.cover_attach_id) || 0,
					status: this.form.status === 0 ? 0 : 1,
					stick: this.form.stick === 1 ? 1 : 0,
					tag_ids: tagIds,
					content: content,
				}).then((res) => {
					this.saving = false;
					if (res.data.code === 200) {
						message.success(res.data.message || '修改成功');
						this.$emit('saved', {
							...this.form,
							title,
							cover: this.coverPreviewUrl,
							tag_ids: tagIds,
							content,
							isHot: content.isHot,
							subtitle: content.subtitle,
							features: content.features,
							stats: content.stats,
							tags: content.features.map((item) => item.title).filter(Boolean),
						});
						this.$emit('update:visible', false);
						return;
					}
					this.$baseFunc.checkState(res.data);
				}).catch((err) => {
					this.saving = false;
					if (err?.data) this.$baseFunc.checkState(err.data);
				});
			},
		},
	};
</script>

<style scoped lang="less">
	.g-tpl-settings__cover {
		display: flex;
		align-items: flex-start;
		gap: 16px;
	}

	.g-tpl-settings__cover-preview {
		flex-shrink: 0;
		width: 96px;
		aspect-ratio: 17 / 25;
		overflow: hidden;
		border-radius: 6px;
		background: #f5f7fa;
		border: 1px solid #eef0f4;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.g-tpl-settings__cover-empty {
		font-size: 12px;
		color: #bbb;
	}

	.g-tpl-settings__cover-actions {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		padding-top: 4px;
	}

	.g-tpl-settings__cover-tip {
		width: 100%;
		margin: 4px 0 0;
		font-size: 12px;
		color: #999;
		line-height: 1.4;
	}

	.g-tpl-settings__group {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}

	.g-tpl-settings__row {
		display: grid;
		grid-template-columns: 64px 1fr 1fr;
		gap: 8px;
		align-items: center;
		padding: 12px;
		background: #f8f9fb;
		border-radius: 8px;
	}

	.g-tpl-settings__row--stat {
		grid-template-columns: 1fr;
		align-items: stretch;
	}

	.g-tpl-settings__row-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.g-tpl-settings__row-label {
		font-size: 13px;
		color: #666;
		white-space: nowrap;
	}

	.g-tpl-settings__icon-preview {
		color: #285af9;
		font-size: 14px;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-tpl-settings-modal {
		.edit-modal-scroll();

		.g-tpl-settings__file-input {
			position: absolute;
			width: 0;
			height: 0;
			opacity: 0;
			overflow: hidden;
			pointer-events: none;
		}
	}
</style>
